import type { ReactNode } from 'react';
import { useState } from 'react';
import { Drawer } from 'antd';
import { ClearOutlined, EyeInvisibleOutlined, EyeOutlined, FieldTimeOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';

import { Badge, Button } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { KHONG_CO, ngayGioVN, ngayVN, soVN } from '../../../../lib/dinh-dang';
import { anKhoa, loaiLicense, tenProjectLicense, thietBiDangBind, trangThaiUser } from '../../nghiep-vu/license';
import type { LicenseRpa, UserOpenCore } from '../../types';
import { BadgeLoaiLicense, BadgeTrangThaiLicense, HanLicense } from './nhan';

/**
 * Chi tiết một User License (`UserLicenseDrawer` của native): User, Workflow được dùng, License, máy đang bind và lịch
 * sử máy đã reset. Thao tác ở chân ngăn theo quyền `rpa:license:update`; thiếu quyền thì ẩn.
 */
export function NganLicense({
  license,
  user,
  workflows,
  suaDuoc,
  onClose,
  onGiaHan,
  onKhoa,
  onReset,
}: {
  license: LicenseRpa;
  user: UserOpenCore | undefined;
  workflows: readonly { id: string; name: string }[];
  suaDuoc: boolean;
  onClose: () => void;
  onGiaHan: () => void;
  onKhoa: () => void;
  onReset: () => void;
}) {
  const { t, locale } = useT();
  const [hienKhoa, setHienKhoa] = useState(false);
  const status = trangThaiUser(license);
  const may = thietBiDangBind(license);
  const lichSu = license.devices.filter((d) => d !== may);
  const biKhoa = status === 'blocked';

  return (
    <Drawer
      open
      onClose={onClose}
      placement="right"
      size="min(640px, 100vw)"
      destroyOnHidden
      title={license.opencore_username || user?.username || t('rpaLicense.chuaGanUser')}
      footer={
        suaDuoc ? (
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" size="md" icon={<ClearOutlined />} disabled={!may} onClick={onReset}>
              {t('rpaLicense.resetThietBi')}
            </Button>
            {status !== 'revoked' && (
              <Button variant="outline" size="md" icon={biKhoa ? <UnlockOutlined /> : <LockOutlined />} onClick={onKhoa}>
                {biKhoa ? t('rpaLicense.moKhoa') : t('rpaLicense.khoa')}
              </Button>
            )}
            <Button variant="primary" size="md" icon={<FieldTimeOutlined />} onClick={onGiaHan}>
              {t('rpaLicense.giaHanNut')}
            </Button>
          </div>
        ) : undefined
      }
    >
      <Phan tieuDe={t('rpaLicense.ngan.user')}>
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Dong nhan={t('rpaLicense.ngan.username')}>{license.opencore_username || user?.username}</Dong>
          <Dong nhan={t('rpaLicense.ngan.email')}>{user?.email || license.opencore_email}</Dong>
          <Dong nhan={t('rpaLicense.ngan.userId')}>
            {license.opencore_user_id ? <code className="break-all text-xs">{license.opencore_user_id}</code> : null}
          </Dong>
          <Dong nhan={t('rpaLicense.ngan.projectKhach')}>
            {[tenProjectLicense(license), license.customer_name].filter(Boolean).join(' / ')}
          </Dong>
        </dl>
      </Phan>

      <Phan tieuDe={t('rpaLicense.ngan.workflow', { so: soVN(workflows.length, locale) })}>
        {workflows.length ? (
          <ul className="divide-y divide-default rounded-lg border border-default">
            {workflows.map((w) => (
              <li key={w.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span className="min-w-0 truncate" title={w.name}>
                  {w.name || KHONG_CO}
                </span>
                <code className="shrink-0 truncate text-xs text-content-muted">{w.id}</code>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-content-muted">{t('rpaLicense.ngan.khongCoWorkflow')}</p>
        )}
      </Phan>

      <Phan tieuDe={t('rpaLicense.ngan.license')}>
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Dong nhan={t('rpaLicense.ngan.trangThai')}>
            <BadgeTrangThaiLicense status={status} />
          </Dong>
          <Dong nhan={t('rpaLicense.ngan.loai')}>
            <BadgeLoaiLicense loai={loaiLicense(license)} />
          </Dong>
          <Dong nhan={t('rpaLicense.ngan.ngayCap')}>{ngayVN(license.created_at)}</Dong>
          <Dong nhan={t('rpaLicense.ngan.ngayHet')}>
            <HanLicense license={license} />
          </Dong>
          <Dong nhan={t('rpaLicense.ngan.ghiChu')}>{license.note}</Dong>
          <Dong nhan={t('rpaLicense.ngan.maNoiBo')}>
            <span className="inline-flex items-center gap-1.5">
              <code className="break-all text-xs">{hienKhoa ? license.license_key : anKhoa(license.license_key)}</code>
              <Button
                variant="ghost"
                size="sm"
                icon={hienKhoa ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                aria-label={hienKhoa ? t('rpaLicense.ngan.anMa') : t('rpaLicense.ngan.hienMa')}
                title={hienKhoa ? t('rpaLicense.ngan.anMa') : t('rpaLicense.ngan.hienMa')}
                onClick={() => setHienKhoa((x) => !x)}
              />
            </span>
          </Dong>
        </dl>
        {license.access_sync_error && <p className="mt-2 text-xs text-warning-700">{license.access_sync_error}</p>}
      </Phan>

      <Phan tieuDe={t('rpaLicense.ngan.thietBi')}>
        {license.devicesStatus === 'unavailable' ? (
          <p className="text-sm text-warning-700">{t('rpaLicense.ngan.thietBiKhongDoc')}</p>
        ) : may ? (
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Dong nhan={t('rpaLicense.ngan.tenMay')}>{may.machineName || may.deviceName}</Dong>
            <Dong nhan={t('rpaLicense.ngan.hwid')}>
              <code className="text-xs">{may.hwidShort}</code>
            </Dong>
            <Dong nhan={t('rpaLicense.ngan.ngayBind')}>{ngayGioVN(may.boundAt || may.activatedAt, locale)}</Dong>
            <Dong nhan={t('rpaLicense.ngan.lanCuoi')}>{ngayGioVN(may.lastSeenAt, locale)}</Dong>
            <Dong nhan={t('rpaLicense.ngan.heDieuHanh')}>{[may.osName, may.osVersion, may.appVersion].filter(Boolean).join(' · ')}</Dong>
          </dl>
        ) : (
          <Badge variant="neutral">{t('rpaLicense.chuaBind')}</Badge>
        )}
        {lichSu.length > 0 && (
          <details className="mt-3 text-xs text-content-secondary">
            <summary className="cursor-pointer">{t('rpaLicense.ngan.lichSuMay', { so: soVN(lichSu.length, locale) })}</summary>
            <ul className="mt-2 space-y-1">
              {lichSu.map((d) => (
                <li key={d.id} className="break-words">
                  {d.machineName || KHONG_CO} · <code>{d.hwidShort}</code>
                  {d.resetAt && ` · ${t('rpaLicense.ngan.daReset', { luc: ngayGioVN(d.resetAt, locale), boi: d.resetBy || KHONG_CO })}`}
                </li>
              ))}
            </ul>
          </details>
        )}
      </Phan>
    </Drawer>
  );
}

function Phan({ tieuDe, children }: { tieuDe: string; children: ReactNode }) {
  return (
    <section className="mb-5">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-muted">{tieuDe}</h3>
      {children}
    </section>
  );
}

function Dong({ nhan, children }: { nhan: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-content-muted">{nhan}</dt>
      <dd className="break-words text-sm text-content-primary">{children || KHONG_CO}</dd>
    </div>
  );
}
